import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import axios from "axios";
import route from "ziggy-js";
import { removeDateFilterShortcutRanges } from "@/Utils/filters";
import { useFilters } from "@/Composables/useFilters";
import { storeToRefs } from "pinia";

export const QUERY_KEY = ["RECURRING_TASKS_VIEWS"];

async function fetchViews() {
    // const { data: r } = await axios.get(
    //     route("api.views.recurring_tasks.all", tenantId)
    // );
    // return r.data;
    return [];
}

async function updateView({ id, filterStates }) {
    const { data: r } = await axios.put(
        route("api.views.recurring_tasks.update", { id }),
        removeDateFilterShortcutRanges(filterStates)
    );
    return r.data;
}

async function renameView({ id, name }) {
    const { data: r } = await axios.patch(
        route("api.views.recurring_tasks.rename", { id }),
        {
            name,
        }
    );
    return r.data;
}

async function addView({ name, tenantId, filterStates }) {
    const { data: r } = await axios.post(
        route("api.views.recurring_tasks.save"),
        {
            name,
            tenant_id: tenantId,
            ...removeDateFilterShortcutRanges(filterStates),
        }
    );
    return r.data;
}

async function deleteView(id) {
    await axios.delete(route("api.views.recurring_tasks.delete", { id }));
}

export function useRecurringTasksViews() {
    const { isLoading, data } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: () => {
            const globalFilterStore = useFilters("global");
            const { filterValues: globalFilterValues } =
                storeToRefs(globalFilterStore);

            const tenantId = globalFilterValues.value.tenant?.[0];
            return fetchViews(tenantId);
        },
        refetchOnMount: true,
    });

    return {
        isLoading,
        customViews: data,
    };
}

export function useUpdateCustomRecurringTasksView({ onSuccess }) {
    const queryClient = useQueryClient();

    const { mutate } = useMutation({
        mutationFn: updateView,
        onSuccess: (updatedView) => {
            queryClient.setQueryData(QUERY_KEY, (cachedViews) =>
                cachedViews.map((cachedView) =>
                    cachedView.id === updatedView.id ? updatedView : cachedView
                )
            );

            onSuccess(updatedView);
        },
    });

    return {
        updateCustomRecurringTasksView: mutate,
    };
}

export function useRenameCustomRecurringTasksView({ onSuccess }) {
    const queryClient = useQueryClient();

    const { mutate } = useMutation({
        mutationFn: renameView,
        onSuccess: (updatedView) => {
            queryClient.setQueryData(QUERY_KEY, (cachedViews) =>
                cachedViews.map((cachedView) =>
                    cachedView.id === updatedView.id ? updatedView : cachedView
                )
            );

            onSuccess(updatedView);
        },
    });

    return {
        renameCustomRecurringTasksView: mutate,
    };
}

export function useAddCustomRecurringTasksView({ onSuccess }) {
    const queryClient = useQueryClient();

    const { mutate } = useMutation({
        mutationFn: addView,
        onSuccess: (savedView) => {
            queryClient.setQueryData(QUERY_KEY, (cachedViews) => [
                ...cachedViews,
                savedView,
            ]);

            onSuccess(savedView);
        },
    });

    return {
        createCustomRecurringTasksView: mutate,
    };
}

export function useDeleteCustomRecurringTasksView({ onSuccess }) {
    const queryClient = useQueryClient();

    const { mutate } = useMutation({
        mutationFn: deleteView,
        onSuccess: (_, deletedViewId) => {
            queryClient.setQueryData(QUERY_KEY, (cachedViews) =>
                cachedViews.filter(
                    (cachedView) => cachedView.id !== deletedViewId
                )
            );

            onSuccess(deletedViewId);
        },
    });

    return {
        deleteCustomRecurringTasksView: mutate,
    };
}
